<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { useProductStore } from '@/@vxapp/Products/useProductStore'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import ProductDialogSimple from '@/components/Dialogs/Products/ProductDialogSimple.vue'
import ProductListToolbar from '@/components/ServiceLists/Products/ProductListToolbar.vue'
import type {
  ProductListQueryParams,
  ProductPaginationMeta,
} from '@/components/ServiceLists/Products/productListTypes'
import ProductsDatatable from '@/components/Tables/Products/ProductsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

type ProductData = Record<string, unknown>
type DialogDisplayTypeValue = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search' | 'showHidden'

definePage({ meta: PageMeta.Products.List.Meta })
setPageTitle(PageMeta.Products.List.Title)

const log = createLogger('ProductsListPage')
const route = useRoute()
const router = useRouter()
const productStore = useProductStore() as any
const notify = useAppNotifications()

const productsDataTable =
  useTemplateRef<InstanceType<typeof ProductsDatatable>>('productsDataTable')

const productData = ref<ProductData | null>(null)
const dialogDisplayType = shallowRef<DialogDisplayTypeValue>(DialogDisplayType.CREATE)
const isProductDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = ref(0)

const defaultQueryParams: ProductListQueryParams = {
  page: PaginationField.QueryParams.page,
  limit: PaginationField.QueryParams.limit,
  sort: '+id',
  search: undefined,
  showHidden: 'true',
  formatStyle: 'List',
}

const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search', 'showHidden']
const allowedLimits = [10, 25, 50, 100]

function normalizeQueryValue(value: unknown): string | undefined {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizeQueryParams(source: Record<string, unknown>): ProductListQueryParams {
  const page = Number.parseInt(normalizeQueryValue(source.page) ?? '', 10)
  const limit = Number.parseInt(normalizeQueryValue(source.limit) ?? '', 10)
  const sort = normalizeQueryValue(source.sort)
  const search = normalizeQueryValue(source.search)?.trim()
  const showHidden = normalizeQueryValue(source.showHidden)

  return {
    ...defaultQueryParams,
    page: Number.isInteger(page) && page > 0 ? page : defaultQueryParams.page,
    limit: allowedLimits.includes(limit) ? limit : defaultQueryParams.limit,
    sort: sort === '+id' || sort === '-id' ? sort : defaultQueryParams.sort,
    search: search || undefined,
    showHidden: showHidden === 'false' ? 'false' : 'true',
  }
}

const queryParams = computed<ProductListQueryParams>(() =>
  normalizeQueryParams(productStore.queryParams ?? defaultQueryParams)
)

function queryParamsMatch(left: ProductListQueryParams, right: ProductListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const routeQueryParams = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(queryParams.value, routeQueryParams)) return false

  productStore.queryParams = {
    ...productStore.queryParams,
    ...routeQueryParams,
  }

  return true
}

function buildRouteQuery() {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  return {
    ...nextQuery,
    ...(queryParams.value.page !== defaultQueryParams.page
      ? { page: String(queryParams.value.page) }
      : {}),
    ...(queryParams.value.limit !== defaultQueryParams.limit
      ? { limit: String(queryParams.value.limit) }
      : {}),
    ...(queryParams.value.sort !== defaultQueryParams.sort ? { sort: queryParams.value.sort } : {}),
    ...(queryParams.value.search ? { search: queryParams.value.search } : {}),
    ...(queryParams.value.showHidden !== defaultQueryParams.showHidden
      ? { showHidden: queryParams.value.showHidden }
      : {}),
  }
}

async function syncRouteQuery() {
  const nextQuery = buildRouteQuery()

  if (JSON.stringify(route.query) === JSON.stringify(nextQuery)) return

  await router.replace({ query: nextQuery })
}

async function fetchProducts() {
  await productsDataTable.value?.fetchProducts()
}

async function updateQueryParams(patch: Partial<ProductListQueryParams>) {
  productStore.queryParams = {
    ...queryParams.value,
    ...patch,
  }

  if (isRouteQueryReady.value) await syncRouteQuery()

  await nextTick()
  await fetchProducts()
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({
    limit: allowedLimits.includes(limit) ? limit : defaultQueryParams.limit,
    page: 1,
  })
}

async function handleVisibilityChange(showHidden: boolean) {
  await updateQueryParams({ showHidden: showHidden ? 'true' : 'false', page: 1 })
}

async function handleClear() {
  await updateQueryParams({ search: undefined, page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

function handleLoaded(pagination: ProductPaginationMeta) {
  resultCount.value = pagination.entriesFound
}

async function handleCreateProduct() {
  try {
    const { responseCode } = await productStore.fetchTemplate()

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load product template', { responseCode })
      notify.error('Failed to load product template.')

      return
    }

    productData.value = cloneReactiveData(productStore.template ?? {})
    dialogDisplayType.value = DialogDisplayType.CREATE
    isProductDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load product template', error)
    notify.error('Failed to load product template.')
  }
}

async function handleSaveProduct(newProductData: ProductData) {
  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await productStore.create(newProductData, false)
        : await productStore.update(newProductData, false)

    const expectedResponseCode =
      dialogDisplayType.value === DialogDisplayType.CREATE ? ResponseCode.CREATED : ResponseCode.OK

    if (response.responseCode !== expectedResponseCode) {
      log.error('Failed to save product', { responseCode: response.responseCode })

      return
    }

    await fetchProducts()
  } catch (error) {
    log.error('Failed to save product', error)
  }
}

async function showEditModal(productGuid: string) {
  try {
    const { responseCode } = await productStore.fetch(productGuid)

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load product', { productGuid, responseCode })
      notify.error('Failed to load product.')

      return
    }

    productData.value = cloneReactiveData(productStore.product ?? {})
    dialogDisplayType.value = DialogDisplayType.EDIT
    isProductDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load product', { productGuid, error })
    notify.error('Failed to load product.')
  }
}

onMounted(async () => {
  await productStore.init()
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await fetchProducts()
})

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return

    await nextTick()
    await fetchProducts()
  },
  { deep: true }
)
</script>

<template>
  <main class="products-workspace">
    <section class="products-workspace__hero">
      <div>
        <span class="products-workspace__eyebrow">Visual catalogue</span>
        <h1>Products worth finding</h1>
        <p>
          Curate product imagery, commercial details, categories, and booking context in one
          browseable workspace.
        </p>
      </div>

      <div class="products-workspace__hero-actions">
        <div class="products-workspace__metric">
          <span>Matching products</span>
          <strong>{{ resultCount.toLocaleString() }}</strong>
        </div>
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          size="large"
          @click="handleCreateProduct"
        >
          New product
        </VBtn>
      </div>
    </section>

    <ProductListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-loading="productStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @visibility-change="handleVisibilityChange"
      @clear="handleClear"
      @refresh="fetchProducts"
    />

    <ProductsDatatable
      ref="productsDataTable"
      :query-params="queryParams"
      @show-edit-modal="showEditModal"
      @page-change="handlePageChange"
      @loaded="handleLoaded"
    />
  </main>

  <ProductDialogSimple
    v-if="productData"
    v-model:is-dialog-visible="isProductDialogVisible"
    :display-type="dialogDisplayType"
    :product-data="productData"
    @create="handleSaveProduct"
    @update="handleSaveProduct"
  />
</template>

<style scoped lang="scss">
.products-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.25rem;
}

.products-workspace__hero {
  display: grid;
  overflow: hidden;
  min-inline-size: 0;
  align-items: end;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 40px;
  background:
    radial-gradient(circle at 78% 20%, rgba(var(--v-theme-primary), 0.14), transparent 27%),
    rgb(var(--v-theme-surface));
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: clamp(1.5rem, 4vw, 3.25rem);
}

.products-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.75rem;
  font-weight: 700;
}

.products-workspace h1 {
  max-inline-size: 44rem;
  margin: 0.55rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(2.35rem, 6vw, 4.4rem);
  font-weight: 700;
  letter-spacing: -0.055em;
  line-height: 0.98;
}

.products-workspace__hero p {
  max-inline-size: 42rem;
  margin: 1rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 1rem;
  line-height: 1.55;
}

.products-workspace__hero-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.products-workspace__hero-actions :deep(.v-btn) {
  min-block-size: 3rem;
  border-radius: 16px;
}

.products-workspace__metric {
  display: grid;
  min-inline-size: 8rem;
  gap: 0.25rem;
  text-align: end;
}

.products-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  font-weight: 700;
}

.products-workspace__metric strong {
  color: rgb(var(--v-theme-primary));
  font-size: 1.8rem;
  letter-spacing: -0.04em;
}

@media (max-width: 767px) {
  .products-workspace__hero {
    align-items: start;
    border-radius: 28px;
    grid-template-columns: minmax(0, 1fr);
  }

  .products-workspace__hero-actions {
    justify-content: space-between;
  }

  .products-workspace__metric {
    text-align: start;
  }
}

@media (max-width: 424px) {
  .products-workspace__hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .products-workspace__hero-actions :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
